import React, { memo, useEffect, useState } from 'react';
import { StyledIndicator, StyledBoxIndicator } from './OnBehalfIndicator.style';
import { PeopleAlt } from '@mui/icons-material';
import {
  UptimeTypography,
  UptimeBox,
  UptimeGeneralDialog,
  HlxButton,
  UptimeWrapperContainer,
} from '@uptime/uptime-component';
import { useTranslation } from 'react-i18next';
import { RootState } from 'redux/store';
import { useSelector } from 'react-redux';
import { Error } from '@mui/icons-material';
import { useTheme } from 'styled-components';
import DefaultUptimeTheme from 'utils/lumosTheme';
import { useOnBehalf, useUserData } from 'hooks';
import { PORTAL_CONFIG } from 'constants/index';
import { HomePath } from 'utils/routes';

export const HIDE_TOP_NAV_OPTIONS = {
  TRUE: 'true',
  PRIMARY: 'primary',
  SECONDARY: 'secondary',
};

export interface IOnBehalfIndicatorProps { };

const OnBehalfIndicator: React.FC<IOnBehalfIndicatorProps> = () => {
  const { t } = useTranslation('home');
  const { onEndOnBehalf } = useOnBehalf();
  const theme = useTheme() as typeof DefaultUptimeTheme;
  const onBehalfReducer = useSelector((state: RootState) => state.onBehalfReducer);
  const { isOnBehalf, userOnBehalf, isManager } = onBehalfReducer;
  const isManagerOnBehalf = isOnBehalf && isManager;
  const endModalDescription = isManagerOnBehalf
    ? t('home:onBehalfOf.modal.end.description.manager')
    : t('home:onBehalfOf.modal.end.description.user');
  const isHomePage = window.location.pathname === HomePath;
  const [isOpenEndOnBehalfModal, setIsOpenEndOnBehalfModal] = useState(false);
  const { hideTopNav } = JSON.parse(
    sessionStorage.getItem(PORTAL_CONFIG) || '{}',
  );
  const isHidePrimaryTopNav = [
    HIDE_TOP_NAV_OPTIONS.TRUE,
    HIDE_TOP_NAV_OPTIONS.PRIMARY,
  ].includes(hideTopNav);
  const [isHaveSecondaryTopNav, setIsHaveSecondaryTopNav] = useState(false);
  const handleEndOnBehalfModal = () => {
    onEndOnBehalf({ navigateToHome: true });
    window.location.reload();
  };
  const currentUser = useUserData();

  useEffect(() => {
    const globalSecondaryMenu = document.querySelector('.marketplace-menu'); 
    if(!isOnBehalf) return;
    setIsHaveSecondaryTopNav(!!globalSecondaryMenu);
  }, [window.location.pathname, isOnBehalf, currentUser, document.querySelector('.marketplace-menu')]);

  return (
    <>
      <StyledIndicator id='dv-indicator-container' isHidePrimaryTopNav={isHidePrimaryTopNav} isHomePage={isHomePage} isHaveSecondaryTopNav={isHaveSecondaryTopNav}>
        <UptimeWrapperContainer>
          <StyledBoxIndicator
            flexDirection={{ xxs: 'column', sm: 'row' }}
            justifyContent="center"
            alignItems="center"
          >
            <UptimeBox display="flex" alignItems="center" paddingLeft="24px">
              <PeopleAlt />
              <UptimeTypography id='txt-indicator-description' textAlign="center" padding="0 16px 0 8px" kind="regular" className="indicator-description">
                {t('home:onBehalfOf.indicator.title', {
                  name: userOnBehalf?.name || `${userOnBehalf?.first_name} ${userOnBehalf?.last_name}`,
                  email: userOnBehalf?.email,
                })}
              </UptimeTypography>
            </UptimeBox>
            <UptimeBox margin="10px">
              <HlxButton
                id='btn-indicator-end'
                style={{ minWidth: 80, fontSize: 14, height: 'auto' }}
                label={t('home:onBehalfOf.indicator.endButton')}
                onClick={() => setIsOpenEndOnBehalfModal(true)}
              />
            </UptimeBox>
          </StyledBoxIndicator>
        </UptimeWrapperContainer>
      </StyledIndicator>

      <UptimeGeneralDialog
        id='dv-end_behalf_dialog-title'
        isOpen={isOpenEndOnBehalfModal}
        handleClose={() => setIsOpenEndOnBehalfModal(false)}
        title={t('home:onBehalfOf.modal.end.title')}
        content={
          <UptimeBox
            pr={20}
            pl={20}
            display="flex"
            flexDirection="column"
            justifyContent="center"
          >
            <UptimeBox textAlign="center" pb={1}>
              <Error
                htmlColor={theme.uptimeColors.generalColors.state.warning}
                fontSize="large"
              />
            </UptimeBox>
            <UptimeBox textAlign="center">
              <UptimeTypography id='txt-end_behalf_dialog-description'>
                {endModalDescription}
              </UptimeTypography>
            </UptimeBox>
          </UptimeBox>
        }
        buttonSubmit={
          <HlxButton
            id='btn-end_behalf_dialog-submit'
            label={t('home:onBehalfOf.modal.end.submitButton')}
            onClick={handleEndOnBehalfModal}
          ></HlxButton>
        }
        cancelLabel={t('home:onBehalfOf.modal.end.cancelButton')}
        commonStyles={{ width: '481px' }}
        justifyContentAction="center"
        headerBackgroundDefault={false}
      />
    </>
  );
};

export default memo(OnBehalfIndicator);
